import React, { useContext, useEffect } from "react";
import ComponentsService from "../../services/ComponentsService";
import {
  ComponentTemplateDto,
  ComponentType,
} from "@growth-machine-llc/stridist-api-client";
import { useTemplateListFilterContext } from "../../contexts/TemplateLibraryFilterContext";
import {
  Box,
  List,
  ListItem,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { EditorProgramContext } from "../new-editor/hooks";
import ComponentTemplateListItem from "./ComponentTemplateListItem";
import SidebarTemplate, {
  TEMPLATE_SIDEBAR_WIDTH,
} from "./sidebar/SidebarTemplate";
import { useTemplateSidebar } from "../../contexts/TemplateSidebarContext";
import { useComponentTemplateDialog } from "../../contexts/TemplateDialogContext";
import useInfiniteScrollQuery from "../../hooks/useInfiniteScroll";
import { LoadMoreButton } from "../button/LoadMoreButton";
import { JourneyAnchors } from "../../constants";
import { JourneyRunContext } from "../../contexts/JourneyRunContext";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.selected.light,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    justifyContent: "flex-start",
  },

  body: {
    padding: theme.spacing(0, 3),
    overflowY: "auto",
  },

  template: {
    display: "flex",
    alignItems: "center",
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1),
    width: "100%",
    backgroundColor: "transparent",
    transition: "background-color 0.3s ease",
  },

  previewContainer: {
    padding: theme.spacing(2),
    maxWidth: 1100,
    marginInline: "auto",
    pointerEvents: "none",
  },
  listContainer: {
    padding: theme.spacing(2),
    width: "100%",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 2,
  },
  preview: {},
}));

export interface ComponentTemplateLibraryProps {
  componentType: ComponentType;
}

const perPage = 5;
export const COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY =
  "component-templates-library";

const ComponentTemplateLibrary = (props: ComponentTemplateLibraryProps) => {
  const { componentType } = props;
  const { isOpen: isSidebarOpen, openSidebar } = useTemplateSidebar();

  const { openDialog } = useComponentTemplateDialog();
  const { isRunning } = useContext(JourneyRunContext);

  const theme = useTheme();
  const downSlg = useMediaQuery(theme.breakpoints.down("slg"));
  const downMd = useMediaQuery(theme.breakpoints.down("md"));

  const s = useStyles();
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<ComponentTemplateDto>(null);

  const { query, sortKey, templatePrivacyType } =
    useTemplateListFilterContext();

  const {
    data: componentTemplatesData,
    ref: templateElementRef,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading: initialLoading,
    isFetching,
  } = useInfiniteScrollQuery({
    queryKey: [
      COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY,
      { componentType, query, sortKey, templatePrivacyType },
    ],
    queryFn: ({ pageParam = 1 }) =>
      ComponentsService.getLibraryComponentTemplates(
        componentType,
        query,
        templatePrivacyType,
        pageParam as number,
        perPage,
        sortKey,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.hasNextPage ? pages.length + 1 : undefined;
    },
  });

  const handleMoreClick = React.useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const templates = React.useMemo(() => {
    const allItems =
      componentTemplatesData?.pages?.flatMap((page) => page.items) || [];

    const seen = new Map<string, Set<string>>();
    const filtered: ComponentTemplateDto[] = [];

    for (const item of allItems) {
      const name = item.title?.trim() || "";

      if (!item.category) {
        filtered.push(item);
        continue;
      }

      const category = item.category;

      if (!seen.has(category)) {
        seen.set(category, new Set());
      }

      if (!seen.get(category).has(name)) {
        seen.get(category).add(name);
        filtered.push(item);
      }
    }

    return filtered;
  }, [componentTemplatesData?.pages]);

  React.useEffect(() => {
    if (templates?.length > 0) {
      setSelectedTemplate(templates[0]);
      return;
    }
  }, [templates]);

  const handleTemplateClick = React.useCallback(
    (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
      editMode?: boolean,
    ) => {
      const {
        dataset: { templateId },
      } = event.currentTarget;
      event.stopPropagation();
      event.preventDefault();

      const template = templates?.find((it) => it.id === templateId);
      setSelectedTemplate(template);
      if (!downSlg) {
        openSidebar();
      } else {
        openDialog(template);
      }
    },
    [templates, setSelectedTemplate, selectedTemplate, downSlg],
  );

  const handleOpenDialog = React.useCallback(
    (template: ComponentTemplateDto, editMode?: boolean) => {
      openDialog(template, editMode);
    },
    [],
  );

  useEffect(() => {
    if (isRunning && selectedTemplate && !downMd) {
      openSidebar();
    }
  }, [isRunning, selectedTemplate, openSidebar, downMd]);

  const isLoading = initialLoading || (isFetching && !isFetchingNextPage);

  const SkeletonList = ({ count = perPage }) => {
    return (
      <List component="div" disablePadding sx={{ width: "100%" }}>
        {Array.from({ length: count }).map((_, index) => (
          <ListItem key={index} className={s.template}>
            <Box
              sx={{
                display: "flex",
                padding: theme.spacing(1, 0),
                width: "100%",
              }}
            >
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{
                  width: theme.spacing(20),
                  height: theme.spacing(10.75),
                  marginRight: theme.spacing(2),
                  borderRadius: theme.spacing(1.5),
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  marginLeft: theme.spacing(3),
                  padding: theme.spacing(1),
                }}
              >
                <Skeleton animation="wave" height={30} width={"60%"} />
                <Skeleton animation="wave" height={20} width={"40%"} />
              </Box>
            </Box>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          position: "absolute",
          marginInline: "auto",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            position: "relative",
          }}
        >
          <Box
            sx={{
              paddingInline: downSlg ? 0 : !isSidebarOpen ? 4 : 0,
              width: 800,
              marginY: 2,
              zIndex: theme.zIndex.appBar,
              ml: downSlg ? "auto" : isSidebarOpen ? 0 : `200px`,
              mr: downSlg
                ? "auto"
                : !isSidebarOpen
                  ? `-${TEMPLATE_SIDEBAR_WIDTH}px`
                  : 0,
              transitionProperty: "margin-left, margin-right",
              transitionDuration: "500ms",
              transitionTimingFunction: "cubic-bezier(0.23, 1, 0.32, 1)",
            }}
          >
            <Box className={s.listContainer}>
              {initialLoading ? (
                <SkeletonList />
              ) : templates?.length > 0 ? (
                <>
                  <List component="div" disablePadding sx={{ width: "100%" }}>
                    {templates.map((item, index) => (
                      <Box className={s.template} key={item.id}>
                        <ComponentTemplateListItem
                          type={componentType}
                          className={
                            index === 0 &&
                            JourneyAnchors.FIRST_COMPONENT_TEMPLATE
                          }
                          component={item}
                          selected={
                            selectedTemplate?.id === item.id && isSidebarOpen
                          }
                          onContentClick={handleTemplateClick}
                          onContentDoubleClick={() => {
                            setSelectedTemplate(item);
                            handleOpenDialog(item);
                          }}
                          data-template-id={item.id}
                          onEdit={() => handleOpenDialog(item, true)}
                          hideArrow={downSlg}
                          hideImage={downMd}
                        />
                      </Box>
                    ))}
                    {isLoading && <SkeletonList />}
                    {hasNextPage && (
                      <div
                        ref={templateElementRef}
                        style={{ height: 1, background: "transparent" }}
                      />
                    )}
                    {hasNextPage && (
                      <LoadMoreButton
                        onClick={handleMoreClick}
                        fullWidth
                        disabled={isLoading}
                        loading={isFetchingNextPage}
                      />
                    )}
                  </List>
                </>
              ) : (
                <Typography variant="body2">
                  You don't have any templates for current filters.
                </Typography>
              )}
            </Box>
          </Box>
          {!downSlg && (
            <EditorProgramContext.Provider value={{ programId: 0 }}>
              {selectedTemplate && (
                <SidebarTemplate
                  content={selectedTemplate}
                  type={componentType}
                />
              )}
            </EditorProgramContext.Provider>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ComponentTemplateLibrary;
